import { useMsal } from "@azure/msal-react";
import { useMemo, useSyncExternalStore } from "react";
import type { UserInfo } from "types/user";

import { AUTH_BYPASS } from "../App";
import { getSnapshot, subscribe } from "../hooks/useRoleOverride";
import { useMe } from "../services/users/useMe";
import { ADMIN_GROUP_ID, ADMIN_USERS } from "./authConfig";

const DEV_USER: UserInfo = {
  userId: "dev-user-001",
  displayName: "Dev User",
  email: "dev@sj.se",
  roles: ["Admin", "CaseWorker"],
  functionalGroupIds: [],
};

function getBypassFunctionalGroupIds(): number[] {
  const globalScope = globalThis as {
    __E2E_FUNCTIONAL_GROUP_IDS__?: unknown;
  };
  const candidate = globalScope.__E2E_FUNCTIONAL_GROUP_IDS__;
  if (!Array.isArray(candidate)) {
    return [];
  }

  return Array.from(
    new Set(
      candidate
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value)),
    ),
  );
}

export interface UseUserResult {
  user: UserInfo | null;
  isRolesLoading: boolean;
  isRolesError: boolean;
  refetchRoles: () => void;
}

export function useUser(): UseUserResult {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- AUTH_BYPASS is a build-time constant
  const msal = AUTH_BYPASS ? { accounts: [] } : useMsal();
  const account = (
    msal as {
      accounts: Array<{
        idTokenClaims?: Record<string, unknown>;
        localAccountId: string;
        name?: string;
        username: string;
      }>;
    }
  ).accounts[0];

  const roleOverride = useSyncExternalStore(subscribe, getSnapshot);

  // Fetch backend-resolved roles (includes UserRoleMappings + group mappings)
  /* eslint-disable react-hooks/rules-of-hooks -- AUTH_BYPASS is a build-time constant, never changes at runtime */
  const {
    data: meData,
    isPending: isMePending,
    isError: isMeError,
    refetch: refetchMe,
  } = AUTH_BYPASS
    ? {
        data: undefined,
        isPending: false,
        isError: false,
        refetch: () => Promise.resolve(),
      }
    : useMe(!!account);
  /* eslint-enable react-hooks/rules-of-hooks */

  const user = useMemo(() => {
    let baseUser: UserInfo | null;

    if (AUTH_BYPASS) {
      baseUser = {
        ...DEV_USER,
        functionalGroupIds: getBypassFunctionalGroupIds(),
      };
    } else if (!account) {
      return null;
    } else {
      // Use backend-resolved roles when available (includes UserRoleMappings),
      // fall back to token-based resolution while /me is loading
      let roles: string[];
      if (meData?.roles && meData.roles.length > 0) {
        roles = meData.roles;
      } else {
        // Fallback: resolve from token claims until /me responds
        const appRoles = (account.idTokenClaims?.roles as string[]) ?? [];
        const groups = (account.idTokenClaims?.groups as string[]) ?? [];
        roles = [...appRoles];
        if (groups.includes(ADMIN_GROUP_ID) && !roles.includes("Admin")) {
          roles.push("Admin");
        }
        if (
          roles.length === 0 &&
          ADMIN_USERS.includes(account.username.toLowerCase())
        ) {
          roles.push("Admin");
        }
      }

      // Implicit Beredare baseline: ALL authenticated users get Beredare.
      // Roles are additive — Gruppmedlem/Admin users also get Beredare capabilities.
      // Hierarchy: Admin ⊇ Gruppmedlem ⊇ Beredare
      if (!roles.includes("Beredare")) {
        roles.push("Beredare");
      }

      baseUser = {
        userId: meData?.userId ?? account.localAccountId,
        displayName: meData?.displayName ?? account.name ?? account.username,
        email: meData?.email ?? account.username,
        roles,
        functionalGroupIds: meData?.functionalGroupIds ?? [],
      };
    }

    // Apply role override (Admin-only) — return ONLY the override role so UI
    // visibility reflects the previewed role. Admin is NOT kept in the array;
    // the backend middleware handles admin API access via X-Role-Override header.
    if (roleOverride && (baseUser.roles ?? []).includes("Admin")) {
      if (roleOverride === "__none__") {
        // __none__ simulates "no explicit roles" — Beredare is still the baseline minimum.
        return { ...baseUser, roles: ["Beredare"], functionalGroupIds: [] };
      }
      return {
        ...baseUser,
        roles: [roleOverride],
      };
    }

    return baseUser;
  }, [account, roleOverride, meData]);

  return {
    user,
    isRolesLoading: isMePending && !meData,
    isRolesError: isMeError,
    refetchRoles: () => {
      void refetchMe();
    },
  };
}
